import { AnalyticsBrowser } from '@segment/analytics-next';
import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'src/common/slices';
import { anonymousId } from 'src/common/tracking/data';
import { initSessionReplay } from 'src/common/tracking/session-replay';
import { measurePerformanceSync } from 'src/common/utils/performance-tracking';
import { useFeatureIsOn, useFeatureValue } from '../growthbook';
const writeKey = process.env.REACT_APP_SEGMENT_ANALYTICS_WRITE_KEY ?? '';
export const analytics = writeKey ? AnalyticsBrowser.load({
  writeKey
}) : null;
export const useSegmentAnalytics = () => {
  const isLoggedOutTrackingEnabled = useFeatureIsOn('is-logged-out-tracking-enabled');
  const isPerformanceTrackingEnabled = useFeatureIsOn('page-load-tracking');
  const HomepageVariants = useFeatureValue('new-homepage', 'control');
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    data: userData
  } = useGetUserQuery();
  const userId = userData?.data?.user?._id;
  const location = useLocation();
  const [referrer, setReferrer] = useState(document.referrer);
  useEffect(() => {
    const initializeAnalytics = async () => {
      if (!analytics) {
        captureException(new Error('Failed to initialise frontend analytics'), {
          level: 'warning',
          extra: {
            analyticsWriteKeyDefined: Boolean(writeKey)
          },
          tags: {
            component: useSegmentAnalytics.name
          }
        });
        return;
      }
      if (!isLoggedOutTrackingEnabled) return;
      await analytics.setAnonymousId(anonymousId);
      await initSessionReplay(analytics);
      setIsInitialized(true);
    };
    void initializeAnalytics().catch(err => captureException(err, {
      level: 'warning',
      tags: {
        component: useSegmentAnalytics.name
      },
      extra: {
        analyticsWriteKeyDefined: Boolean(writeKey),
        isLoggedOutTrackingEnabled
      }
    }));
  }, [isLoggedOutTrackingEnabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: don't want to trigger for URL search or hash changes
  useEffect(() => {
    if (!analytics || !isLoggedOutTrackingEnabled || !isInitialized) return;
    const fullUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const pageProperties = {
      referrer,
      url: fullUrl,
      path: location.pathname === '/' ? HomepageVariants === 'control' ? '/' : `/${HomepageVariants}` : location.pathname,
      title: document.title,
      search: location.search,
      browser_language: navigator?.language ?? null
    };

    // Add performance data if enabled
    if (isPerformanceTrackingEnabled) {
      const performanceData = measurePerformanceSync();
      if (performanceData) {
        Object.assign(pageProperties, {
          total_load_time: performanceData.totalLoadTime ?? null,
          first_contentful_paint: performanceData.firstContentfulPaint ?? null,
          largest_contentful_paint: performanceData.largestContentfulPaint ?? null
        });
      }
    }
    setReferrer(fullUrl);
    void analytics.page(pageProperties) // Creates "Page Viewed" event in Amplitude
    .catch(err => captureException(err, {
      level: 'warning',
      tags: {
        component: useSegmentAnalytics.name
      }
    }));
  }, [isLoggedOutTrackingEnabled, location.pathname, isInitialized, isPerformanceTrackingEnabled]);
  useEffect(() => {
    if (!analytics || !isLoggedOutTrackingEnabled || !isInitialized) return;
    if (userId) {
      void analytics.identify(userId);
    }
  }, [isLoggedOutTrackingEnabled, userId, isInitialized]);
};